import { useCallback, useMemo } from 'react';
import { QueryVariables } from '../types';
import { useToast } from '@/shadcn/hooks/use-toast';

interface UsePaginationProps {
  queryVariables: QueryVariables;
  updateUrlParams: (variables: Partial<QueryVariables>) => void;
  handlePagination: () => void;
}

interface PaginationState {
  before: string | null;
  after: string | null;
}

export const usePagination = ({ queryVariables, updateUrlParams, handlePagination }: UsePaginationProps) => {
  const { toast } = useToast();

  const paginationState = useMemo<PaginationState>(() => ({
    before: queryVariables.before,
    after: queryVariables.after
  }), [queryVariables.before, queryVariables.after]);

  const onPageChange = useCallback((cursor: string | null, direction: 'next' | 'prev') => {
    try {
      const newQueryVariables = {
        ...queryVariables,
        before: direction === 'prev' ? cursor : null,
        after: direction === 'next' ? cursor : null,
      };

      updateUrlParams(newQueryVariables);
      handlePagination();
    } catch (error) {
      console.error('Error updating pagination:', error);
      toast({
        variant: 'destructive',
        title: 'Unable to Load More Results',
        description: 'We encountered an issue while trying to load more results. Please refresh the page and try again.',
      });
    }
  }, [queryVariables, updateUrlParams, handlePagination, toast]);

  return {
    paginationState,
    onPageChange
  };
};
